/**
 * Session Request Manager
 * Handles request deduplication and prevents multiple simultaneous session requests
 */

import { authClient } from "../auth/client";
import logger from "logger";

export interface SessionRequestState {
  isLoading: boolean;
  lastRequest: number;
  requestCount: number;
  errors: Error[];
  retryCount: number;
  queuedRequests: number;
  cancelledRequests: number;
}

export interface SessionRequestManager {
  getSession(): Promise<any | null>;
  isRequestInFlight(): boolean;
  isRequestCancelled(): boolean;
  cancelPendingRequests(): void;
  getRequestState(): SessionRequestState;
}

class SessionRequestManagerImpl implements SessionRequestManager {
  private pendingRequest: Promise<any | null> | null = null;
  private currentAbortController: AbortController | null = null;
  private queuedRequestCount = 0;
  private requestState: SessionRequestState = {
    isLoading: false,
    lastRequest: 0,
    requestCount: 0,
    errors: [],
    retryCount: 0,
    queuedRequests: 0,
    cancelledRequests: 0,
  };

  private readonly maxRetries = 3;
  private readonly baseDelay = 1000; // 1 second

  async getSession(): Promise<any | null> {
    // If there's already a request in flight, increment queued count and return the same promise
    if (this.pendingRequest) {
      this.queuedRequestCount++;
      this.requestState.queuedRequests = this.queuedRequestCount;
      return this.pendingRequest;
    }

    // Create new AbortController for this request
    this.currentAbortController = new AbortController();

    this.requestState.isLoading = true;
    this.requestState.lastRequest = Date.now();
    this.requestState.requestCount++;
    this.requestState.queuedRequests = this.queuedRequestCount;

    this.pendingRequest = this.executeSessionRequest();

    try {
      const result = await this.pendingRequest;
      this.requestState.isLoading = false;
      this.requestState.retryCount = 0;
      this.requestState.errors = [];
      return result;
    } catch (error) {
      this.requestState.isLoading = false;

      // Track cancelled requests
      if (
        error instanceof Error &&
        (error.message === "Request was cancelled" ||
          error.name === "AbortError")
      ) {
        this.requestState.cancelledRequests++;
      } else {
        this.requestState.errors.push(error as Error);
        logger.error("Session request failed:", error);
      }

      throw error;
    } finally {
      this.pendingRequest = null;
      this.currentAbortController = null;
      this.queuedRequestCount = 0;
      this.requestState.queuedRequests = 0;
    }
  }

  private async executeSessionRequest(): Promise<any | null> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        // Check if request was cancelled before making the call
        if (this.currentAbortController?.signal.aborted) {
          throw new Error("Request was cancelled");
        }

        const session = await authClient.getSession();
        return session.data;
      } catch (error) {
        lastError = error as Error;
        this.requestState.retryCount = attempt + 1;

        // Don't retry if request was cancelled
        if (this.currentAbortController?.signal.aborted) {
          throw new Error("Request was cancelled");
        }

        if (attempt < this.maxRetries) {
          // Exponential backoff with jitter
          const delay =
            this.baseDelay * Math.pow(2, attempt) + Math.random() * 1000;

          // Use AbortController signal for cancellable delay
          await this.cancellableDelay(delay);
        }
      }
    }

    throw lastError || new Error("Session request failed after all retries");
  }

  isRequestInFlight(): boolean {
    return this.pendingRequest !== null;
  }

  isRequestCancelled(): boolean {
    return this.currentAbortController?.signal.aborted ?? false;
  }

  cancelPendingRequests(): void {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }

    if (this.pendingRequest) {
      this.pendingRequest = null;
      this.requestState.isLoading = false;
    }
  }

  private async cancellableDelay(ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(resolve, ms);

      if (this.currentAbortController) {
        this.currentAbortController.signal.addEventListener("abort", () => {
          clearTimeout(timeout);
          reject(new Error("Request was cancelled"));
        });
      }
    });
  }

  getRequestState(): SessionRequestState {
    return { ...this.requestState };
  }
}

// Singleton instance
export const sessionRequestManager = new SessionRequestManagerImpl();
